/**
 * MH-05 Bản đồ thiết bị.
 *
 * Bản đồ CHỈ tải thiết bị nằm trong khung nhìn hiện tại và tải lại khi người dùng
 * di chuyển hoặc thu phóng, có độ trễ 500 mili giây để tránh gửi liên tiếp nhiều
 * yêu cầu (mục 4.4 và Bảng 12.1). Khi thu nhỏ thì GOM NHÓM theo mật độ — không bao
 * giờ vẽ 5.000 điểm cùng lúc.
 *
 * Nền bản đồ: Leaflet + ô bản đồ OpenStreetMap qua thành phần dùng chung
 * `BanDoNen` (URL ô bản đồ và chú thích bản quyền đọc từ `VITE_MAP_TILE_URL`,
 * `VITE_MAP_ATTRIBUTION`). Tâm, mức thu phóng và khung (bắc/nam/đông/tây) lấy từ
 * sự kiện `moveend` của Leaflet; hợp đồng gọi API (khung nhìn, mức thu phóng, gom
 * nhóm, trễ `TRE_KHUNG_NHIN_MS`) giữ nguyên. Bản đồ không tự `fitBounds` ở màn
 * hình này vì dữ liệu tải THEO khung nhìn — tự căn khung sẽ đổi khung nhìn và gây
 * tải lại liên tiếp.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viThietBi } from '../ngon-ngu/vi-thiet-bi';
import { thietBiApi, type DongThietBi } from '../api/thiet-bi-api';
import { dungHoan, dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong, TrangThaiRongLoc } from '../thanh-phan/TrangThaiManHinh';
import { NhanTrangThai } from '../thanh-phan/NhanTrangThai';
import { BanDoNen, TAM_BAN_DO_MAC_DINH, type DiemBanDo, type KhungNhinBanDo } from '../thanh-phan/BanDoNen';
import { dungPhien } from '../trang-thai/ngu-canh-phien';

/** Độ trễ tải lại sau khi người dùng dừng di chuyển hoặc thu phóng. */
export const TRE_KHUNG_NHIN_MS = 500;

interface KhungNhin {
  tamViDo: number;
  tamKinhDo: number;
  zoom: number;
  /** Khung thật từ Leaflet; chưa có (lần đầu, vừa bấm nút) thì ước lượng theo zoom. */
  bien?: { bacDo: number; namDo: number; dongDo: number; tayDo: number };
}

const BAN_DAU: KhungNhin = { ...TAM_BAN_DO_MAC_DINH };
const ZOOM_NHO_NHAT = 7;
const ZOOM_LON_NHAT = 16;

/** Nửa chiều rộng khung nhìn theo độ, giảm một nửa mỗi mức thu phóng. */
function nuaKhung(zoom: number): number {
  return 2 / 2 ** (zoom - 8);
}

/**
 * Khung nhìn vừa đủ chứa các toạ độ, theo thang thu phóng của Leaflet (Web Mercator, ô
 * 256px): ở mức z một điểm ảnh ≈ 360 / (256 · 2^z) độ kinh. Giả định khung bản đồ tối
 * thiểu 1000 × 400 px và đệm 30% để điểm không sát mép.
 */
export function khungNhinTheoDiem(
  ds: readonly (readonly [number, number])[],
  rongPx = 1000,
  caoPx = 400,
): KhungNhin | null {
  if (ds.length === 0) return null;
  const dsVi = ds.map((x) => x[0]);
  const kinh = ds.map((x) => x[1]);
  const tamViDo = (Math.min(...dsVi) + Math.max(...dsVi)) / 2;
  const tamKinhDo = (Math.min(...kinh) + Math.max(...kinh)) / 2;
  // Mercator giãn vĩ độ theo 1/cos(vĩ độ).
  const heSo = 1 / Math.cos((tamViDo * Math.PI) / 180);
  const nhipVi = Math.max((Math.max(...dsVi) - Math.min(...dsVi)) * 1.3 * heSo, 0.01);
  const nhipKinh = Math.max((Math.max(...kinh) - Math.min(...kinh)) * 1.3, 0.01);
  const zVi = Math.log2((caoPx * 360) / (256 * nhipVi));
  const zKinh = Math.log2((rongPx * 360) / (256 * nhipKinh));
  const zoom = Math.max(ZOOM_NHO_NHAT, Math.min(ZOOM_LON_NHAT, Math.floor(Math.min(zVi, zKinh))));
  return { tamViDo, tamKinhDo, zoom };
}

export function MH05BanDoThietBi(): ReactNode {
  const phien = dungPhien();
  const [kn, datKn] = useState<KhungNhin>(BAN_DAU);
  // Mở trang: căn khung nhìn theo vị trí thiết bị của tỉnh (một lần), thay vì tâm cố định
  // trong mã — tâm mặc định chỉ dùng khi tỉnh chưa có thiết bị nào có toạ độ.
  const daCan = useRef(false);
  const khungDau = useRef<KhungNhin>(BAN_DAU);
  useEffect(() => {
    if (daCan.current) return;
    daCan.current = true;
    const huy = new AbortController();
    thietBiApi
      .banDo({ mucThuPhong: 10, nguonId: phien.nguonIdHienTai }, huy.signal)
      .then((dl) => {
        const kn0 = khungNhinTheoDiem([
          ...dl.nhom.map((n) => [n.viDo, n.kinhDo] as const),
          ...dl.diem
            .filter((x) => x.viDo !== null && x.kinhDo !== null)
            .map((x) => [x.viDo as number, x.kinhDo as number] as const),
        ]);
        if (kn0) {
          khungDau.current = kn0;
          datKn(kn0);
        }
      })
      .catch(() => undefined);
    return () => huy.abort();
  }, [phien.nguonIdHienTai]);
  const knHoan = dungHoan(kn, TRE_KHUNG_NHIN_MS);
  const [dangChon, datDangChon] = useState<DongThietBi | null>(null);

  const nua = nuaKhung(knHoan.zoom);
  const bien = knHoan.bien ?? {
    bacDo: knHoan.tamViDo + nua,
    namDo: knHoan.tamViDo - nua,
    dongDo: knHoan.tamKinhDo + nua * 1.4,
    tayDo: knHoan.tamKinhDo - nua * 1.4,
  };
  const kq = dungTaiDuLieu<Awaited<ReturnType<typeof thietBiApi.banDo>>>({
    tai: (signal) =>
      thietBiApi.banDo(
        {
          bacDo: bien.bacDo,
          namDo: bien.namDo,
          dongDo: bien.dongDo,
          tayDo: bien.tayDo,
          mucThuPhong: knHoan.zoom,
          nguonId: phien.nguonIdHienTai,
        },
        signal,
      ),
    laRong: (d) => d.diem.length === 0 && d.nhom.length === 0,
    // Khung nhìn là điều kiện lọc: rỗng nghĩa là khung nhìn không có thiết bị.
    dangLoc: true,
    phuThuoc: [bien.bacDo, bien.namDo, bien.dongDo, bien.tayDo, knHoan.zoom, phien.nguonIdHienTai],
  });

  // Danh sách, bảng nhóm, thẻ thông tin chỉ hiện khi có dữ liệu; bản đồ luôn
  // giữ lại (không gỡ khi tải lại) và vẽ dữ liệu gần nhất.
  const d = kq.trangThai === 'co-du-lieu' ? kq.duLieu : null;
  const duLieuVe = kq.duLieu;
  const diemVe: DiemBanDo[] = useMemo(() => {
    if (!duLieuVe) return [];
    const nhom: DiemBanDo[] = duLieuVe.nhom.map((n, i) => ({
      khoa: `nhom-${i}`,
      viDo: n.viDo,
      kinhDo: n.kinhDo,
      soThietBi: n.soThietBi,
      soMatKetNoi: n.soMatKetNoi,
      tieuDe: dienVao(viThietBi.tongQuan.nhomThietBi, { so: n.soThietBi, mat: n.soMatKetNoi }),
    }));
    const diem: DiemBanDo[] = [];
    for (const t of duLieuVe.diem) {
      if (t.viDo === null || t.kinhDo === null) continue;
      const matKetNoi = t.trangThaiHienThi === 'mat_ket_noi';
      const trangThai =
        vi.trangThaiThietBi[t.trangThaiHienThi as keyof typeof vi.trangThaiThietBi] ?? t.trangThaiHienThi;
      diem.push({
        khoa: t.id,
        viDo: t.viDo,
        kinhDo: t.kinhDo,
        soThietBi: 1,
        soMatKetNoi: matKetNoi ? 1 : 0,
        tieuDe: `${t.tenThietBi ?? t.cumLoaId} — ${trangThai}`,
        onChon: () => datDangChon(t),
      });
    }
    return [...nhom, ...diem];
  }, [duLieuVe]);

  // Leaflet báo khung nhìn thật sau mỗi lần di chuyển/thu phóng.
  const khiDoiKhungNhin = useCallback((k: KhungNhinBanDo) => {
    datKn({
      tamViDo: k.tamViDo,
      tamKinhDo: k.tamKinhDo,
      zoom: k.zoom,
      bien: { bacDo: k.bacDo, namDo: k.namDo, dongDo: k.dongDo, tayDo: k.tayDo },
    });
  }, []);

  // Nút di chuyển dịch theo nửa khung ước lượng của mức thu phóng hiện tại; bỏ
  // khung cũ để Leaflet báo lại khung thật sau khi setView.
  const di = (dViDo: number, dKinhDo: number): void =>
    datKn((c) => {
      const n = nuaKhung(c.zoom);
      return { tamViDo: c.tamViDo + dViDo * n, tamKinhDo: c.tamKinhDo + dKinhDo * n, zoom: c.zoom };
    });

  return (
    <section className="man-hinh" data-man-hinh="MH-05">
      <nav className="vet-duong-dan" aria-label={vi.dieuHuong.thietBi}>
        <Link to="/thiet-bi">{vi.dieuHuong.thietBi}</Link> / <span>{vi.banDo.tieuDe}</span>
      </nav>
      <h1>{vi.banDo.tieuDe}</h1>
      <p className="chu-thich">{vi.banDo.taiTheoKhungNhin}</p>

      <div className="ban-do__dieu-khien" role="group" aria-label={vi.banDo.diChuyen}>
        <button type="button" className="nut nut--nho" onClick={() => di(0.5, 0)}>
          ↑
        </button>
        <button type="button" className="nut nut--nho" onClick={() => di(-0.5, 0)}>
          ↓
        </button>
        <button type="button" className="nut nut--nho" onClick={() => di(0, -0.5)}>
          ←
        </button>
        <button type="button" className="nut nut--nho" onClick={() => di(0, 0.5)}>
          →
        </button>
        <button
          type="button"
          className="nut nut--nho"
          onClick={() =>
            datKn((c) => ({
              tamViDo: c.tamViDo,
              tamKinhDo: c.tamKinhDo,
              zoom: Math.min(ZOOM_LON_NHAT, c.zoom + 1),
            }))
          }
        >
          {vi.banDo.phongTo}
        </button>
        <button
          type="button"
          className="nut nut--nho"
          onClick={() =>
            datKn((c) => ({
              tamViDo: c.tamViDo,
              tamKinhDo: c.tamKinhDo,
              zoom: Math.max(ZOOM_NHO_NHAT, c.zoom - 1),
            }))
          }
        >
          {vi.banDo.thuNho}
        </button>
        <span>
          {vi.banDo.mucThuPhong}: {kn.zoom}
        </span>
      </div>

      {/* Bản đồ luôn gắn để giữ thao tác kéo/thu phóng của người dùng giữa các
          lần tải lại theo khung nhìn. */}
      <BanDoNen
        className="ban-do"
        diem={diemVe}
        nhan={vi.banDo.tieuDe}
        khungNhin={kn}
        onDoiKhungNhin={khiDoiKhungNhin}
        minZoom={ZOOM_NHO_NHAT}
        maxZoom={ZOOM_LON_NHAT}
      />

      {kq.trangThai === 'dang-tai' && !kq.duLieu ? (
        <KhungXuong soDong={4} soCot={4} taiLau={kq.taiLau} />
      ) : null}
      {kq.trangThai === 'loi' && kq.loi ? <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} /> : null}
      {kq.trangThai === 'rong' ? <TrangThaiRong /> : null}
      {kq.trangThai === 'rong-loc' ? <TrangThaiRongLoc onXoaLoc={() => datKn(khungDau.current)} /> : null}

      {d ? (
        <>
          <p className="chu-thich" data-vung="che-do-ban-do">
            {d.gomNhom ? vi.banDo.gomNhom : vi.banDo.tungThietBi}
          </p>

          {/* Chế độ gom nhóm: bảng số liệu là đường dẫn tương đương cho người
              dùng bàn phím và người dùng trình đọc màn hình — điểm trên bản đồ
              không đọc được. */}
          {d.nhom.length > 0 ? (
            <table className="bang" aria-label={vi.banDo.gomNhom}>
              <thead>
                <tr>
                  <th scope="col">{vi.bieuMau.truong.viDo}</th>
                  <th scope="col">{vi.bieuMau.truong.kinhDo}</th>
                  <th scope="col">{vi.banDo.soThietBi}</th>
                  <th scope="col">{vi.banDo.trucTuyen}</th>
                  <th scope="col">{vi.trangThaiThietBi.mat_ket_noi}</th>
                </tr>
              </thead>
              <tbody>
                {d.nhom.slice(0, 30).map((n, i) => (
                  <tr key={i}>
                    <td>{n.viDo.toFixed(3)}</td>
                    <td>{n.kinhDo.toFixed(3)}</td>
                    <td>{n.soThietBi}</td>
                    <td>{n.soTrucTuyen}</td>
                    <td>{n.soMatKetNoi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          {/* Bản đồ không thao tác được bằng bàn phím, nên danh sách thiết bị
              trong khung nhìn là đường dẫn tương đương, bấm được bằng Tab. */}
          {d.diem.length > 0 ? (
            <ul className="ban-do__danh-sach">
              {d.diem.slice(0, 50).map((t) => (
                <li key={t.id}>
                  <button type="button" className="nut nut--lien-ket" onClick={() => datDangChon(t)}>
                    {t.tenThietBi ?? t.cumLoaId}
                  </button>{' '}
                  <NhanTrangThai ma={t.trangThaiHienThi} />
                </li>
              ))}
            </ul>
          ) : null}

          {dangChon ? (
            <div className="the-thong-tin" data-vung="the-thong-tin-ban-do">
              <h2>{dangChon.tenThietBi ?? dangChon.cumLoaId}</h2>
              <p>
                <NhanTrangThai ma={dangChon.trangThaiHienThi} />
              </p>
              <Link className="nut" to={`/thiet-bi/${dangChon.id}`}>
                {vi.thietBi.moChiTiet}
              </Link>
              <button type="button" className="nut" onClick={() => datDangChon(null)}>
                {vi.chung.dong}
              </button>
            </div>
          ) : null}

          {/* Thiết bị chưa có toạ độ liệt kê riêng kèm nhắc nhở bổ sung toạ độ. */}
          {d.soKhongCoToaDo > 0 ? (
            <aside className="ban-do__khong-toa-do" data-vung="khong-co-toa-do">
              <h2>
                {vi.banDo.khongCoToaDo}: {d.soKhongCoToaDo} {vi.banDo.soThietBi}
              </h2>
              <p>{vi.banDo.khongCoToaDoNhac}</p>
              <Link to="/thiet-bi">{vi.thietBi.tieuDeDanhSach}</Link>
            </aside>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
